import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import isArchived from 'archive/isArchived';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as DataTableConfigActions from 'notebook/data-tables/DataTableConfigActions';
import {isDataTableConfigValidForManualEntry} from 'notebook/data-tables/DataTableConfigEditUtil';
import DataTableConfigStore from 'notebook/data-tables/DataTableConfigStore';
import * as DataTableConstants from 'notebook/data-tables/DataTableConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {ASSAY_TYPES} from 'results/assay-schemas/AssaySchemaConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import SelectDropdown, {closeSelectDropdown} from 'ui-library/dropdowns/SelectDropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import APIUtil from 'util/APIUtil';
import {WorkflowSelector} from 'workflows/reduceWorkflows';

interface DataTableConfigSelectorProps {
  fileId: string;
  onDataTableConfigChange: (args: {
    dataTableConfig: DataTableConstants.DataTableConfig | null;
    cidIdMap: CidIdMap;
    error: string | null;
  }) => void;
  selectedDataTableConfig?: DataTableConstants.DataTableConfig;
  type: string;
}

interface ConnectedProps {
  areSamplesLoading: boolean;
  configs: Array<DataTableConstants.FromServerDataTableConfig> | null;
}

interface State {
  tableCid: string;
}

class DataTableConfigSelectorConnector extends DataConnector<DataTableConfigSelectorProps, ConnectedProps> {
  stores = [DataTableConfigStore];

  enhanceProps = (): ConnectedProps => {
    const configs = this.dataTableConfigStore().getDataTableConfigs();
    if (this.props.type === ModelTypeConstants.DATA_TABLE_CONFIG) {
      return {areSamplesLoading: false, configs};
    }
    const workflowSelector = new WorkflowSelector(this.reduxState().models.workflows);
    const stageRuns = workflowSelector.getStageRunsForEntry(this.props.fileId);
    const stageRunSamplesById = workflowSelector.getStageRunSampleById();
    return {
      areSamplesLoading: stageRuns
        ? !DataTableUtil.isDataTableCreationAllowed(stageRuns, stageRunSamplesById)
        : false,
      configs,
    };
  };
}

/**
 * A React component that allows selecting a data table config to insert into an entry.
 *
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */

export class DataTableConfigSelector extends React.Component<
  DataTableConfigSelectorProps & ConnectedProps,
  State
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    fileId: PropTypes.string.isRequired,
    onDataTableConfigChange: PropTypes.func.isRequired,
    selectedDataTableConfig: DataTableConstants.DATA_TABLE_CONFIG_SHAPE,

    type: PropTypes.oneOf([ModelTypeConstants.DATA_TABLE, ModelTypeConstants.DATA_TABLE_CONFIG]).isRequired,

    // Provided by connect.
    // True if we haven't loaded the stage run samples for this stage entry. If so, we should wait
    // until they are loaded, since otherwise we won't know which stage run samples are eligible to
    // add to the new table.
    areSamplesLoading: PropTypes.bool.isRequired,
    configs: PropTypes.arrayOf(DataTableConstants.DATA_TABLE_CONFIG_SHAPE.isRequired),
  };

  dropdown?: SelectDropdown | null;

  state: State = {
    tableCid: _.uniqueId('data-table-config-insert-modal'),
  };

  componentDidMount(): void {
    // Re-fetch in case new configs were added.
    DataTableConfigActions.fetchAllDataTableConfigs();
  }

  _isStandaloneDataTable(): boolean {
    return !this._isForStageEntry();
  }

  _isForStageEntry(): boolean {
    return [ModelTypeConstants.STAGE_CONFIGURATION, ModelTypeConstants.STAGE_ENTRY].includes(
      APIUtil.apiIdToType(this.props.fileId)
    );
  }

  _handleDataTableConfigChange = (value: string): void => {
    this.props.onDataTableConfigChange(this.getStateWithPreview(value));
    closeSelectDropdown(this.dropdown!);
  };

  getStateWithPreview(configId: string): {
    dataTableConfig: DataTableConstants.DataTableConfig | null;
    cidIdMap: CidIdMap;
    error: string | null;
  } {
    let cidIdMap = {};
    let dataTableConfig = null;
    let error = null;
    if (configId != null) {
      const serverDataTableConfig = this.props.configs!.find((config) => config.id === configId);
      if (serverDataTableConfig!.assaySchema.schemaType === ASSAY_TYPES.ASSAY_RESULT) {
        const isStandalone = this._isStandaloneDataTable();
        const isWatchingInputTable = false;
        const watchedRegistrationProvenanceUuids: Array<string> = [];
        dataTableConfig = DataTableUtil.cloneAndPrepareDataTableConfig(
          serverDataTableConfig!,
          isStandalone,
          isWatchingInputTable,
          watchedRegistrationProvenanceUuids,
          cidIdMap
        );
        if (dataTableConfig.columns.length === 0) {
          // Rather than filter this config out in "isUsable" below, we let the user know why they can't insert
          error = 'The selected result table has no columns.';
        }
      }
    }
    if (dataTableConfig && !this._isDataTableConfigUsable(dataTableConfig)) {
      dataTableConfig = null;
      cidIdMap = {};
      error = null;
    }
    return {dataTableConfig, cidIdMap, error};
  }

  _isDataTableConfigUsable(
    dataTableConfig: DataTableConstants.FromServerDataTableConfig | DataTableConstants.DataTableConfig
  ): boolean {
    return (
      dataTableConfig.assaySchema.schemaType === ASSAY_TYPES.ASSAY_RESULT &&
      isDataTableConfigValidForManualEntry(dataTableConfig) &&
      // Non-standalone data tables need `linkType` to specify if the table links to batches or entities.
      (this._isStandaloneDataTable() || dataTableConfig.linkType != null) &&
      !isArchived(dataTableConfig.assaySchema)
    );
  }

  render(): JSX.Element {
    const configId =
      this.props.selectedDataTableConfig == null ? null : this.props.selectedDataTableConfig.id;
    if (this.props.configs == null || this.props.areSamplesLoading) {
      return (
        <div className="text-center">
          <HorizontalThrobber />
        </div>
      );
    }
    return (
      <div>
        <SelectDropdown
          disabled={false}
          displayName={
            this.props.selectedDataTableConfig == null ? 'Select...' : this.props.selectedDataTableConfig.name
          }
          closeOnInsideClick={true}
          closeOnOutsideClick={true}
          menu={
            <SearchableMenu
              onSelect={this._handleDataTableConfigChange}
              optionDatas={this.props.configs
                .filter((config) => this._isDataTableConfigUsable(config))
                .map((config) => ({
                  value: config.id,
                  name: config.name,
                }))}
              selectedValue={configId}
              width={Menu.WIDTHS.FULL}
            />
          }
          ref={(node) => {
            this.dropdown = node;
          }}
        />
        {this.props.selectedDataTableConfig && (
          <FormGroup className="u-spacing-top">
            <Label>Preview</Label>
            <div className="modal-mediocre-tableWrapper">
              <SimpleTableEditable
                {...standaloneProps}
                table={DataTableUtil.convertDataTableConfigToTable(
                  this.props.selectedDataTableConfig,
                  true /* indicateCustomColumns */
                )}
                cid={this.state.tableCid}
                locked={true}
                viewer={true}
                hideFormulaBox={true}
                icon="table"
                enableFormulas={false}
                hasPlaceholderRows={true}
                storeState={() => {}}
                tableLabel={MODEL_NAMES.Result}
                isLeftAlignColumnHeader={true}
              />
            </div>
          </FormGroup>
        )}
      </div>
    );
  }
}

/**
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export default DataTableConfigSelectorConnector.connect(DataTableConfigSelector);
